import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.js';
import type { Validation } from './types.js';

export type ValidationStatus = NonNullable<Validation['status']>;

export interface CreateValidationPayload {
  validatorUid: string;
  requestComment: string | null;
}

export interface DecideValidationPayload {
  decision: 'accept' | 'refuse';
  comment: string | null;
}

/** Validations the caller is the validator on (i.e. "à faire"). */
export function useMyValidations(status: ValidationStatus | 'all' = 'pending') {
  return useQuery<Validation[]>({
    queryKey: ['my-validations', status],
    queryFn: () =>
      apiFetch<Validation[]>(`/validations?status=${encodeURIComponent(status)}&limit=100`),
    staleTime: 15_000,
  });
}

/** Per-invoice validation history (for the requester's view). */
export function useInvoiceValidations(primaryKey: string) {
  return useQuery<Validation[]>({
    queryKey: ['invoice-validations', primaryKey],
    queryFn: () =>
      apiFetch<Validation[]>(`/invoices/${encodeURIComponent(primaryKey)}/validations`),
    enabled: Boolean(primaryKey),
    staleTime: 30_000,
  });
}

/**
 * Request a validation on one invoice. Optimistically prepends a synthetic
 * `pending` row to the invoice's validation history so the requester sees
 * immediate feedback; rolls back on error.
 */
export function useRequestValidation(primaryKey: string) {
  const qc = useQueryClient();
  return useMutation<
    Validation,
    Error,
    CreateValidationPayload,
    { previous: Validation[] | undefined }
  >({
    mutationFn: (payload) =>
      apiFetch<Validation>(`/invoices/${encodeURIComponent(primaryKey)}/validations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: ['invoice-validations', primaryKey] });
      const previous = qc.getQueryData<Validation[]>(['invoice-validations', primaryKey]);
      const optimistic: Validation = {
        validationId: `pending-${Date.now()}`,
        primaryKey,
        tenantId: '',
        status: 'pending',
        requesterUid: 'pending',
        requesterEmail: null,
        requesterDisplayName: null,
        requestComment: payload.requestComment,
        requestedAt: new Date().toISOString(),
        validatorUid: payload.validatorUid,
        validatorEmail: null,
        validatorDisplayName: null,
        decision: null,
        decisionComment: null,
        decidedAt: null,
        lastReminderAt: null,
        remindersSent: 0,
      };
      qc.setQueryData<Validation[]>(
        ['invoice-validations', primaryKey],
        [optimistic, ...(previous ?? [])],
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(['invoice-validations', primaryKey], ctx.previous);
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['invoice-validations', primaryKey] }),
  });
}

/**
 * Validator decides (accept/refuse). Optimistically removes the row from
 * the pending list; reverts on error.
 */
export function useDecideValidation() {
  const qc = useQueryClient();
  return useMutation<
    Validation,
    Error,
    { validationId: string; payload: DecideValidationPayload },
    { previous: Validation[] | undefined }
  >({
    mutationFn: ({ validationId, payload }) =>
      apiFetch<Validation>(`/validations/${encodeURIComponent(validationId)}/decide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    onMutate: async ({ validationId }) => {
      await qc.cancelQueries({ queryKey: ['my-validations', 'pending'] });
      const previous = qc.getQueryData<Validation[]>(['my-validations', 'pending']);
      qc.setQueryData<Validation[]>(
        ['my-validations', 'pending'],
        (previous ?? []).filter((v) => v.validationId !== validationId),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous !== undefined) {
        qc.setQueryData(['my-validations', 'pending'], ctx.previous);
      }
    },
    onSettled: (data) => {
      void qc.invalidateQueries({ queryKey: ['my-validations'] });
      if (data) {
        void qc.invalidateQueries({ queryKey: ['invoice-validations', data.primaryKey] });
      }
    },
  });
}
